import { z } from 'zod';

export const AssetMergeAssetTypeSchema = z.enum(['ENDPOINT', 'WEBPAGE']);
export const AssetMergeSourceSchema = z.enum([
  'AUTOMATIC',
  'MANUAL',
  'COPILOT',
]);
export const AssetMergeModeSchema = z.enum(['WAIT', 'IMMEDIATE']);
export const AssetMergePublicModeSchema = AssetMergeModeSchema;
export const AssetMergeSurvivorKindSchema = z.enum(['EXISTING', 'NEW']);
export const AssetMergeHistoryStatusSchema = z.enum([
  'AWAITING_FINISH',
  'MERGE_DONE',
  'FAILED',
  'REVERTED',
  'REVERTING',
]);

export const MergeSurvivorExistingSchema = z.object({
  kind: z.literal('EXISTING'),
  id: z.string().min(1),
});

export const MergeSurvivorNewSchema = z.object({
  kind: z.literal('NEW'),
  path: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
  hash: z.string().min(1).optional(),
});

export const MergeSurvivorSchema = z.discriminatedUnion('kind', [
  MergeSurvivorExistingSchema,
  MergeSurvivorNewSchema,
]);

export const MergeAssetsPublicBodySchema = z.object({
  assetType: AssetMergeAssetTypeSchema,
  survivor: MergeSurvivorSchema,
  loserIds: z.array(z.string().min(1)).min(1),
  source: z.enum(['MANUAL', 'COPILOT']),
  mode: AssetMergeModeSchema.optional(),
  acknowledgedDisruption: z.literal(true).optional(),
});

export const MergeAssetsInputSchema = z.object({
  applicationId: z.string().min(1),
  assetType: AssetMergeAssetTypeSchema,
  survivor: MergeSurvivorSchema,
  loserIds: z.array(z.string().min(1)).min(1),
  source: AssetMergeSourceSchema,
  mode: AssetMergeModeSchema.default('WAIT'),
  acknowledgedDisruption: z.boolean().optional(),
});

export const ListAwaitingMergesInputSchema = z.object({
  applicationId: z.string().min(1),
  assetIds: z.array(z.string().min(1)).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const ListAwaitingMergesBodySchema = z.object({
  assetIds: z.array(z.string().min(1)).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const ForceFinishMergeInputSchema = z.object({
  applicationId: z.string().min(1),
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
  acknowledgedDisruption: z.literal(true),
});

export const ForceFinishMergeBodySchema = z.object({
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
  acknowledgedDisruption: z.literal(true),
});

export const AssetMergeHistoryItemSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  userId: z.string().nullable(),
  assetType: AssetMergeAssetTypeSchema,
  source: AssetMergeSourceSchema,
  mode: AssetMergeModeSchema,
  status: AssetMergeHistoryStatusSchema,
  survivorId: z.string().nullable(),
  survivorKind: AssetMergeSurvivorKindSchema,
  loserIds: z.array(z.string()),
  canonicalHash: z.string().nullable(),
  inputHash: z.string(),
  finishedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const MergeAssetsResultSchema = z.object({
  history: AssetMergeHistoryItemSchema,
  survivorId: z.string(),
  loserIds: z.array(z.string()),
});

export const MergeAssetsMergedSchema = MergeAssetsResultSchema.extend({
  status: z.literal('merged'),
});

export const MergeAssetsNeedsForceFinishSchema = z.object({
  status: z.literal('needs_force_finish'),
  message: z.string().min(1),
  awaitingHistories: z.array(AssetMergeHistoryItemSchema),
});

export const MergeAssetsCallerResultSchema = z.discriminatedUnion('status', [
  MergeAssetsMergedSchema,
  MergeAssetsNeedsForceFinishSchema,
]);

export const AssetMergeAcceptedSchema = z.object({
  status: z.literal('accepted'),
});

export const ListAwaitingMergesResultSchema = z.object({
  histories: z.array(AssetMergeHistoryItemSchema),
  awaitingAssetIds: z.array(z.string()),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
});

export const ForceFinishMergeResultSchema = z.object({
  histories: z.array(AssetMergeHistoryItemSchema),
});

export const RevertAssetMergeInputSchema = z.object({
  applicationId: z.string().min(1),
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
  acknowledgedDisruption: z.literal(true),
});

export const RevertAssetMergeBodySchema = z.object({
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
  acknowledgedDisruption: z.literal(true),
});

export const RevertAssetMergeResultSchema = z.object({
  histories: z.array(AssetMergeHistoryItemSchema),
});

export const PreviewAssetMergeRevertInputSchema = z.object({
  applicationId: z.string().min(1),
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
});

export const PreviewAssetMergeRevertPartySchema = z.object({
  assetId: z.string().min(1),
  type: AssetMergeAssetTypeSchema,
  path: z.string().min(1).optional(),
  domain: z.string().min(1).optional(),
  method: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
});

export const PreviewAssetMergeRevertResultSchema = z.object({
  historyIds: z.array(z.string()),
  restored: z.array(PreviewAssetMergeRevertPartySchema),
  remaining: z.array(PreviewAssetMergeRevertPartySchema),
});

export const ListAssetMergeHistoryBodySchema = z.object({
  filters: z
    .object({
      source: AssetMergeSourceSchema.optional(),
      assetType: AssetMergeAssetTypeSchema.optional(),
      status: AssetMergeHistoryStatusSchema.optional(),
      historyIds: z.array(z.string().min(1)).optional(),
      assetIds: z.array(z.string().min(1)).optional(),
      fromDate: z.string().optional(),
      toDate: z.string().optional(),
    })
    .optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const ListAssetMergeHistoryResultSchema = z.object({
  histories: z.array(AssetMergeHistoryItemSchema),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
});

export const StartAssetMergeDetectPublicSourceSchema = z.enum([
  'MANUAL',
  'COPILOT',
]);

export const StartAssetMergeDetectBodySchema = z.object({
  assetType: AssetMergeAssetTypeSchema,
  source: StartAssetMergeDetectPublicSourceSchema,
  acknowledgedDisruption: z.literal(true).optional(),
});

export const StartAssetMergeDetectInputSchema = z.object({
  applicationId: z.string().min(1),
  assetType: AssetMergeAssetTypeSchema,
  source: AssetMergeSourceSchema,
  acknowledgedDisruption: z.boolean().optional(),
  applyMode: AssetMergeModeSchema.optional(),
});

export const StartAssetMergeDetectStartedSchema = z.object({
  status: z.literal('started'),
  runId: z.string().min(1),
  forceFinishedCount: z.number().int().nonnegative(),
});

export const StartAssetMergeDetectNeedsForceFinishSchema = z.object({
  status: z.literal('needs_force_finish'),
  message: z.string().min(1),
  awaitingHistories: z.array(AssetMergeHistoryItemSchema),
});

export const StartAssetMergeDetectAlreadyRunningSchema = z.object({
  status: z.literal('already_running'),
  runId: z.string().min(1).optional(),
});

export const StartAssetMergeDetectSkippedSchema = z.object({
  status: z.literal('skipped'),
});

export const StartAssetMergeDetectResultSchema = z.discriminatedUnion(
  'status',
  [
    StartAssetMergeDetectStartedSchema,
    StartAssetMergeDetectNeedsForceFinishSchema,
    StartAssetMergeDetectAlreadyRunningSchema,
    StartAssetMergeDetectSkippedSchema,
  ],
);
